import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ChevronLeft,
  ChevronRight,
  Check,
  Clock,
  Eye,
  EyeOff,
  ArrowUpDown,
  Loader2,
  Paperclip,
  Pencil,
  Tags,
  X,
} from "lucide-react";
import { createPageUrl } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import AdminPageShell from "@/components/admin/AdminPageShell";
import AdminVideoReorderPanel from "@/components/admin/AdminVideoReorderPanel";
import AdminVideoMaterialEditDialog from "@/components/admin/AdminVideoMaterialEditDialog";
import AdminVideoTagEditDialog from "@/components/admin/AdminVideoTagEditDialog";
import { useToast } from "@/components/ui/use-toast";
import {
  listAdminVideos,
  setAdminVideoPublishState,
  setAdminVideoTitle,
  toAdminApiErrorMessage,
} from "@/api/adminVideos";
import { useRequireAdminRedirect } from "@/hooks/useRequireAdminRedirect";
import { getAdminFeature } from "@/config/adminFeatures";
import {
  SERIES_FILTER_DICTIONARY,
  SERIES_FILTER_ORDER,
  TAG_FILTER_DICTIONARY,
  getDictionaryLabel,
} from "@/config/catalogFilters";

const ADMIN_VIDEO_PAGE_SIZE = 20;
/** `videos.title` は varchar(255)。Lambda 側と同じ上限で先に弾く。 */
const VIDEO_TITLE_MAX_LENGTH = 255;
const feature = getAdminFeature("AdminVideoIndex");

/** 管理画面では非表示カテゴリも含めて全件から選べるようにする。 */
function buildCategoryOptions(language = "ja") {
  const orderedKeys = SERIES_FILTER_ORDER.filter((key) => key in SERIES_FILTER_DICTIONARY);
  const orderedKeySet = new Set(orderedKeys);
  const leftoverKeys = Object.keys(SERIES_FILTER_DICTIONARY).filter(
    (key) => !orderedKeySet.has(key)
  );

  return [...orderedKeys, ...leftoverKeys].map((value) => ({
    value,
    label: getDictionaryLabel(SERIES_FILTER_DICTIONARY, value, language, `Category ${value}`),
  }));
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDuration(seconds) {
  const total = Number(seconds || 0);
  if (!(total > 0)) return "--:--";

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
  }
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** 表示名はDBの `name` を優先。無い場合だけフロントの辞書 → slug の順に落とす。 */
function getVideoTags(video) {
  return (Array.isArray(video.tags) ? video.tags : [])
    .map((entry) => {
      const tag = typeof entry === "string" ? { slug: entry } : entry ?? {};
      const slug = tag.slug ?? null;
      const label =
        tag.name || (slug ? getDictionaryLabel(TAG_FILTER_DICTIONARY, slug, "ja", slug) : "");
      return { key: String(tag.id ?? slug ?? label), label, isActive: tag.isActive !== false };
    })
    .filter((tag) => tag.label);
}

export default function AdminVideoIndex() {
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAdminRedirect();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  // 公開切替・タイトル編集で共通のエラー表示。トーストを見落としてもここに残る。
  const [actionErrorMessage, setActionErrorMessage] = useState(null);
  const [editingTitleVideoId, setEditingTitleVideoId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [tagEditVideoId, setTagEditVideoId] = useState(null);
  const [materialEditVideoId, setMaterialEditVideoId] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);

  const categoryOptions = useMemo(() => buildCategoryOptions(), []);
  const categoryLabelById = useMemo(
    () => new Map(categoryOptions.map((option) => [option.value, option.label])),
    [categoryOptions]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, sort]);

  // 並び替えはカテゴリ単位。「すべて」では対象が決まらないのでモードを抜ける
  useEffect(() => {
    if (categoryFilter === "all") setIsReorderMode(false);
  }, [categoryFilter]);

  // 一覧の中身が入れ替わったら編集状態を閉じる。開いたままだと、
  // 別の動画の行に入力途中のタイトルが残って見える。
  useEffect(() => {
    setEditingTitleVideoId(null);
    setTitleDraft("");
    setTagEditVideoId(null);
    setMaterialEditVideoId(null);
  }, [categoryFilter, sort, currentPage]);

  const isCategorySelected = categoryFilter !== "all";
  const isSortDescending = sort === "newest";
  // カテゴリを指定したときは、そのカテゴリ内の表示順（`sort_order`）で並べる。
  // 既定は降順。「すべて」のときは従来どおり登録日時順。
  const sortParam = isCategorySelected
    ? isSortDescending
      ? "order_desc"
      : "order_asc"
    : sort;
  const sortDescLabel = isCategorySelected ? "表示順（降順）" : "新着順（降順）";
  const sortAscLabel = isCategorySelected ? "表示順（昇順）" : "古い順（昇順）";

  const videoQueryParams = useMemo(() => {
    const params = {
      page: currentPage,
      pageSize: ADMIN_VIDEO_PAGE_SIZE,
      view: "card",
      sort: sortParam,
    };
    if (categoryFilter !== "all") {
      params.categoryId = categoryFilter;
    }
    return params;
  }, [categoryFilter, currentPage, sortParam]);

  const { data: videoResponse, isLoading: videosLoading } = useQuery({
    queryKey: ["admin-videos", videoQueryParams],
    queryFn: () => listAdminVideos(videoQueryParams),
    enabled: canRenderProtectedContent && !isReorderMode,
    placeholderData: keepPreviousData,
  });

  // 表示は「DB更新が成功した」あとにしか変えない。楽観更新を入れると、
  // UPDATE が失敗したときに画面だけ切り替わって見えてしまう。
  const publishMutation = useMutation({
    mutationFn: ({ videoId, nextIsPublished }) =>
      setAdminVideoPublishState(videoId, nextIsPublished),
    onSuccess: async (result) => {
      setActionErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-videos"] });
      toast({
        description: result.isPublished ? "公開しました" : "非公開にしました",
      });
    },
    onError: (error) => {
      const message = toAdminApiErrorMessage(error, "公開状態の変更に失敗しました。");
      setActionErrorMessage(message);
      toast({ variant: "destructive", description: message });
    },
  });

  const titleMutation = useMutation({
    mutationFn: ({ videoId, title }) => setAdminVideoTitle(videoId, title),
    onSuccess: async () => {
      setActionErrorMessage(null);
      // 更新が確定してから編集欄を閉じ、取り直した一覧の値を表示する
      setEditingTitleVideoId(null);
      setTitleDraft("");
      await queryClient.invalidateQueries({ queryKey: ["admin-videos"] });
      toast({ description: "タイトルを変更しました" });
    },
    onError: (error) => {
      // 入力を捨てないよう、編集状態は開いたままにする
      const message = toAdminApiErrorMessage(error, "タイトルの変更に失敗しました。");
      setActionErrorMessage(message);
      toast({ variant: "destructive", description: message });
    },
  });

  const isMutating = publishMutation.isPending || titleMutation.isPending;
  const pendingPublishVideoId = publishMutation.isPending
    ? publishMutation.variables?.videoId
    : null;
  const pendingTitleVideoId = titleMutation.isPending
    ? titleMutation.variables?.videoId
    : null;

  const togglePublishState = (video) => {
    if (isMutating) return;
    setActionErrorMessage(null);
    publishMutation.mutate({ videoId: video.id, nextIsPublished: !video.is_published });
  };

  const startTitleEdit = (video) => {
    if (isMutating) return;
    setActionErrorMessage(null);
    setEditingTitleVideoId(video.id);
    setTitleDraft(video.title ?? "");
  };

  const cancelTitleEdit = () => {
    if (titleMutation.isPending) return;
    setActionErrorMessage(null);
    setEditingTitleVideoId(null);
    setTitleDraft("");
  };

  const saveTitle = (video) => {
    if (isMutating) return;

    const nextTitle = titleDraft.trim();
    // 空白のみは未入力扱い。Lambda 側も同じ判定をするが、通信する前に弾く。
    if (!nextTitle) {
      setActionErrorMessage("動画タイトルを入力してください。");
      return;
    }
    if (nextTitle.length > VIDEO_TITLE_MAX_LENGTH) {
      setActionErrorMessage(`動画タイトルは${VIDEO_TITLE_MAX_LENGTH}文字までです。`);
      return;
    }

    setActionErrorMessage(null);
    titleMutation.mutate({ videoId: video.id, title: nextTitle });
  };

  const videos = videoResponse?.items ?? [];
  // 一覧を取り直すと配列が作り替わるため、id から引き直す
  const tagEditVideo = videos.find((video) => video.id === tagEditVideoId) ?? null;
  const materialEditVideo = videos.find((video) => video.id === materialEditVideoId) ?? null;
  const totalVideoCount = Number(videoResponse?.pagination?.total ?? videos.length);
  const totalPages = Math.max(1, Math.ceil(totalVideoCount / ADMIN_VIDEO_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === safeCurrentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoadingAuth || !canRenderProtectedContent) return null;

  return (
    <AdminPageShell
      icon={feature.icon}
      title={feature.label}
      description="RDS の videos テーブルを参照しています。"
    >
      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-[#e8dccd] bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 sm:w-72">
          <label className="mb-2 block text-sm font-medium text-[#2d1810]">カテゴリ</label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full bg-[#f5ede3]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categoryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-0">
          <label className="mb-2 block text-sm font-medium text-[#2d1810]">並び順</label>
          <Button
            type="button"
            variant="outline"
            onClick={() => setSort((current) => (current === "newest" ? "oldest" : "newest"))}
            className="h-10 w-full justify-center gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3] sm:w-44"
            aria-label={`${isSortDescending ? sortAscLabel : sortDescLabel}に切り替える`}
          >
            {isSortDescending ? (
              <ArrowDownWideNarrow className="h-4 w-4 text-[#8b6f47]" />
            ) : (
              <ArrowUpWideNarrow className="h-4 w-4 text-[#8b6f47]" />
            )}
            {isSortDescending ? sortDescLabel : sortAscLabel}
          </Button>
        </div>

        <div className="min-w-0">
          <label className="mb-2 block text-sm font-medium text-[#2d1810]">表示順の変更</label>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsReorderMode(true)}
            disabled={categoryFilter === "all" || isReorderMode}
            title={
              categoryFilter === "all"
                ? "並び替えはカテゴリを選んでから行えます"
                : undefined
            }
            className="h-10 w-full justify-center gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3] sm:w-44"
          >
            <ArrowUpDown className="h-4 w-4 text-[#8b6f47]" />
            並び替え
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529]">
          カテゴリ: {categoryLabelById.get(categoryFilter) ?? categoryFilter}
        </Badge>
        {!isReorderMode && (
          <Badge className="bg-[#8b6f47] px-3 py-1.5 text-xs text-[#f5ede3]">
            {videosLoading ? "読み込み中" : `${totalVideoCount}本`}
          </Badge>
        )}
      </div>

      {actionErrorMessage && (
        <div
          role="alert"
          className="mb-3 rounded-xl border border-[#d98a7a] bg-[#fdf1ee] px-4 py-3 text-sm text-[#8a3b28]"
        >
          {actionErrorMessage}
        </div>
      )}

      {isReorderMode ? (
        <AdminVideoReorderPanel
          categoryId={categoryFilter}
          categoryLabel={categoryLabelById.get(categoryFilter) ?? categoryFilter}
          onClose={() => setIsReorderMode(false)}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-[#e8dccd] bg-white">
            {videosLoading ? (
              <div className="divide-y divide-[#f0e6d8]">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="flex gap-4 p-4">
                    <Skeleton className="h-20 w-36 flex-shrink-0 rounded-lg bg-[#d4c4b0]" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-4 w-2/3 bg-[#d4c4b0]" />
                      <Skeleton className="h-3 w-1/3 bg-[#d4c4b0]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : videos.length === 0 ? (
              <div className="py-20 text-center text-[#6b5540]">該当する動画がありません。</div>
            ) : (
              <div className="divide-y divide-[#f0e6d8]">
                {videos.map((video) => {
                  const isPublished = Boolean(video.is_published);
                  const isTogglePending = pendingPublishVideoId === video.id;
                  const isEditingTitle = editingTitleVideoId === video.id;
                  const isTitleSavePending = pendingTitleVideoId === video.id;
                  const videoTags = getVideoTags(video);
                  const categoryLabel =
                    video.category_id == null
                      ? "未設定"
                      : categoryLabelById.get(String(video.category_id)) ??
                        `Category ${video.category_id}`;

                  return (
                    <div
                      key={video.id}
                      className="flex flex-col gap-4 p-4 transition-colors hover:bg-[#faf6f1] sm:flex-row"
                    >
                      <Link
                        to={createPageUrl(`VideoPlayer?id=${video.id}`)}
                        className="relative aspect-video w-full flex-shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-[#f0e3d3] to-[#d6c0a0] sm:w-36"
                      >
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs font-medium text-[#6b5540]">
                            No Thumbnail
                          </div>
                        )}
                      </Link>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          {isEditingTitle ? (
                            <div className="min-w-0 flex-1">
                              <Input
                                value={titleDraft}
                                onChange={(event) => setTitleDraft(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    saveTitle(video);
                                  }
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    cancelTitleEdit();
                                  }
                                }}
                                maxLength={VIDEO_TITLE_MAX_LENGTH}
                                disabled={isTitleSavePending}
                                autoFocus
                                aria-label="動画タイトル"
                                className="bg-[#f5ede3] font-semibold text-[#2d1810]"
                              />
                              <p className="mt-1 text-xs text-[#8b6f47]">
                                {titleDraft.length}/{VIDEO_TITLE_MAX_LENGTH}文字
                              </p>
                            </div>
                          ) : (
                            <Link
                              to={createPageUrl(`VideoPlayer?id=${video.id}`)}
                              className="min-w-0 font-semibold text-[#2d1810] transition-colors hover:text-[#8b6f47]"
                            >
                              {video.title || "(タイトル未設定)"}
                            </Link>
                          )}
                          <span className="text-xs text-[#8b6f47]">ID: {video.id}</span>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#6b5540]">
                          <Badge
                            className={
                              isPublished
                                ? "bg-[#4f7a52] text-xs text-[#f5ede3]"
                                : "bg-[#a08e77] text-xs text-[#f5ede3]"
                            }
                          >
                            {isPublished ? "公開中" : "非公開"}
                          </Badge>
                          <Badge variant="outline" className="border-[#d4c4b0] text-xs text-[#5d4529]">
                            {categoryLabel}
                          </Badge>
                          <span>登録日: {formatDate(video.created_at)}</span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(video.duration_seconds)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {video.view_count}
                          </span>
                          {video.is_preview && (
                            <Badge className="bg-[#8b6f47] text-xs text-[#f5ede3]">FREE</Badge>
                          )}
                        </div>

                        {videoTags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {videoTags.map((tag) => (
                              <Badge
                                key={tag.key}
                                variant="outline"
                                className={
                                  tag.isActive
                                    ? "border-[#d4c4b0] text-[10px] text-[#5d4529]"
                                    : "border-[#d98a7a] text-[10px] text-[#8a3b28]"
                                }
                              >
                                {tag.label}
                                {!tag.isActive && "（無効）"}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* 管理操作はこの1行にまとめる。#61 の残りの機能もここへ足す。 */}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {isEditingTitle ? (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                disabled={isMutating || !titleDraft.trim()}
                                onClick={() => saveTitle(video)}
                                className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#7a6040]"
                              >
                                {isTitleSavePending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                                保存
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={isTitleSavePending}
                                onClick={cancelTitleEdit}
                                className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                              >
                                <X className="h-4 w-4 text-[#8b6f47]" />
                                キャンセル
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isMutating}
                              onClick={() => startTitleEdit(video)}
                              className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                            >
                              <Pencil className="h-4 w-4 text-[#8b6f47]" />
                              タイトル編集
                            </Button>
                          )}

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isMutating || isEditingTitle}
                            onClick={() => setTagEditVideoId(video.id)}
                            className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                          >
                            <Tags className="h-4 w-4 text-[#8b6f47]" />
                            タグ編集
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isMutating || isEditingTitle}
                            onClick={() => togglePublishState(video)}
                            className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                          >
                            {isTogglePending ? (
                              <Loader2 className="h-4 w-4 animate-spin text-[#8b6f47]" />
                            ) : isPublished ? (
                              <EyeOff className="h-4 w-4 text-[#8b6f47]" />
                            ) : (
                              <Eye className="h-4 w-4 text-[#8b6f47]" />
                            )}
                            {isPublished ? "非公開にする" : "公開する"}
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isMutating || isEditingTitle}
                            onClick={() => setMaterialEditVideoId(video.id)}
                            className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                          >
                            <Paperclip className="h-4 w-4 text-[#8b6f47]" />
                            資料・アンケートURL変更
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!videosLoading && totalVideoCount > ADMIN_VIDEO_PAGE_SIZE && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <p className="text-xs text-[#6b5540]">
                {totalVideoCount}本中 {(safeCurrentPage - 1) * ADMIN_VIDEO_PAGE_SIZE + 1}-
                {Math.min(safeCurrentPage * ADMIN_VIDEO_PAGE_SIZE, totalVideoCount)}本を表示
              </p>
              <Pagination>
                <PaginationContent className="flex-wrap justify-center gap-2">
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      size="default"
                      onClick={(event) => {
                        event.preventDefault();
                        goToPage(safeCurrentPage - 1);
                      }}
                      className={`gap-1 pl-2.5 ${
                        safeCurrentPage === 1 ? "pointer-events-none opacity-50" : ""
                      }`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>前へ</span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem className="px-2 text-sm font-medium text-[#5d4529] md:hidden">
                    {safeCurrentPage}/{totalPages}
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, index) => {
                    const page = index + 1;
                    return (
                      <PaginationItem key={page} className="hidden md:list-item">
                        <PaginationLink
                          href="#"
                          isActive={page === safeCurrentPage}
                          onClick={(event) => {
                            event.preventDefault();
                            goToPage(page);
                          }}
                          className={
                            page === safeCurrentPage
                              ? "border-[#d4c4b0] text-[#2d1810]"
                              : "text-[#5d4529]"
                          }
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      size="default"
                      onClick={(event) => {
                        event.preventDefault();
                        goToPage(safeCurrentPage + 1);
                      }}
                      className={`gap-1 pr-2.5 ${
                        safeCurrentPage === totalPages ? "pointer-events-none opacity-50" : ""
                      }`}
                    >
                      <span>次へ</span>
                      <ChevronRight className="h-4 w-4" />
                    </PaginationLink>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
      <AdminVideoTagEditDialog
        key={tagEditVideoId ?? "none"}
        video={tagEditVideo}
        open={Boolean(tagEditVideo)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setTagEditVideoId(null);
        }}
      />
      <AdminVideoMaterialEditDialog
        key={materialEditVideoId ? `material-${materialEditVideoId}` : "material-none"}
        video={materialEditVideo}
        open={Boolean(materialEditVideo)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setMaterialEditVideoId(null);
        }}
      />
    </AdminPageShell>
  );
}
